import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import ConsoleLayout from "@/components/equilibrium/ConsoleLayout";
import { fetchEqDiagnostics } from "@/lib/eq";

export default function ConsoleDiagnostics() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    fetchEqDiagnostics().then(setItems).catch(() => setItems([]));
  }, []);

  const filtered = items.filter((d) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      d.name.toLowerCase().includes(s) ||
      d.email.toLowerCase().includes(s) ||
      d.company.toLowerCase().includes(s) ||
      d.problem_statement.toLowerCase().includes(s)
    );
  });

  return (
    <ConsoleLayout>
      <div data-testid="eq-console-diagnostics">
        <div className="num-label">DIAGNOSTICS · INTAKE LEDGER</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl text-axiom-text font-bold tracking-tight">
          Operator diagnostic intake.
        </h1>
        <p className="mt-2 text-axiom-muted text-sm max-w-2xl">
          Every diagnostic submission becomes a provenance record. Confidence and entropy are
          computed at intake; module recommendations are produced from the problem statement.
        </p>

        <div className="mt-6 flex justify-end">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-axiom-dim" />
            <input
              data-testid="eq-diag-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="search..."
              className="bg-axiom-surface border border-white/10 pl-9 pr-4 py-2 text-sm text-axiom-text placeholder:text-axiom-dim focus:border-axiom-accent focus:outline-none w-full sm:w-80"
            />
          </div>
        </div>

        <div className="mt-6 border border-white/10 bg-axiom-bg overflow-x-auto">
          <table className="w-full text-xs" data-testid="eq-diag-table">
            <thead>
              <tr className="text-left bg-axiom-surface/40 border-b border-white/10 font-mono uppercase tracking-wider text-axiom-dim">
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 hidden sm:table-cell">Company</th>
                <th className="px-4 py-3">Conf</th>
                <th className="px-4 py-3">Entropy</th>
                <th className="px-4 py-3 hidden md:table-cell">Suggested</th>
                <th className="px-4 py-3">Email</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr
                  key={d.id}
                  data-testid={`eq-diag-row-${d.id}`}
                  onClick={() => setSel(d)}
                  className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                >
                  <td className="px-4 py-3 text-axiom-dim font-mono">
                    {new Date(d.created_at).toLocaleString(undefined, {
                      month: "short",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-4 py-3 text-axiom-text">{d.name}</td>
                  <td className="px-4 py-3 text-axiom-muted hidden sm:table-cell">{d.company}</td>
                  <td className="px-4 py-3 font-mono text-axiom-accent">
                    {(d.confidence_score * 100).toFixed(1)}%
                  </td>
                  <td className="px-4 py-3 font-mono text-axiom-muted">
                    {d.entropy_estimate.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {d.suggested_modules.map((m) => (
                        <span
                          key={m}
                          className="text-[10px] font-mono uppercase border border-axiom-accent/40 text-axiom-accent px-1.5 py-0.5"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-axiom-muted">{d.email}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-axiom-muted">
                    No diagnostics yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {sel && (
          <div
            data-testid="eq-diag-drawer"
            className="fixed inset-0 z-50 flex"
            onClick={() => setSel(null)}
          >
            <div className="flex-1 bg-axiom-bg/60 backdrop-blur-sm" />
            <div
              className="w-full max-w-xl bg-axiom-surface border-l border-white/10 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <span className="num-label">DIAGNOSTIC.RECORD</span>
                  <button
                    className="text-axiom-muted hover:text-axiom-text"
                    onClick={() => setSel(null)}
                  >
                    ✕
                  </button>
                </div>
                <h2 className="font-display text-2xl text-axiom-text font-bold">{sel.name}</h2>
                <div className="text-xs font-mono text-axiom-accent mt-1">{sel.email}</div>

                <div className="mt-6 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
                  <div className="bg-axiom-bg p-4">
                    <div className="num-label">CONFIDENCE</div>
                    <div className="font-display text-3xl text-axiom-accent mt-1">
                      {(sel.confidence_score * 100).toFixed(1)}%
                    </div>
                  </div>
                  <div className="bg-axiom-bg p-4">
                    <div className="num-label">ENTROPY</div>
                    <div className="font-display text-3xl text-axiom-text mt-1">
                      {sel.entropy_estimate.toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4 text-xs">
                  <KV label="COMPANY" v={sel.company} />
                  <KV label="ROLE" v={sel.role || "—"} />
                  <KV label="TARGET" v={sel.target_module} />
                  <KV label="RECEIVED" v={new Date(sel.created_at).toLocaleString()} />
                </div>

                <div className="mt-6 border-t border-white/10 pt-6">
                  <div className="num-label mb-2">SIGNALS OBSERVED</div>
                  <div className="flex flex-wrap gap-2">
                    {sel.signals_observed.length === 0 ? (
                      <span className="text-xs text-axiom-dim">none recorded</span>
                    ) : (
                      sel.signals_observed.map((s) => (
                        <span
                          key={s}
                          className="text-[10px] font-mono uppercase border border-white/10 text-axiom-muted px-2 py-1"
                        >
                          {s}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                <div className="mt-6 border-t border-white/10 pt-6">
                  <div className="num-label mb-2">PROBLEM STATEMENT</div>
                  <p className="text-sm text-axiom-text whitespace-pre-wrap leading-relaxed">
                    {sel.problem_statement}
                  </p>
                </div>

                <div className="mt-6 border-t border-white/10 pt-6">
                  <div className="num-label mb-2">SUGGESTED MODULES</div>
                  <div className="flex flex-wrap gap-2">
                    {sel.suggested_modules.map((m) => (
                      <span
                        key={m}
                        className="text-xs font-mono uppercase border border-axiom-accent/40 text-axiom-accent px-3 py-1"
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ConsoleLayout>
  );
}

function KV({ label, v }) {
  return (
    <div>
      <div className="num-label">{label}</div>
      <div className="text-axiom-text mt-1 break-words">{v}</div>
    </div>
  );
}
